import React from 'react';

function Resume() {
  function handleExportPdf() {
    window.print();
  }

  return (
    <section className="page resume-page">
      <div className="resume-header">
        <div>
          <h1>Prapti Setty</h1>
          <p className="resume-export-hint">Export or print a clean PDF copy of this page.</p>
        </div>
        <button type="button" className="resume-export-button" onClick={handleExportPdf}>
          Export PDF
        </button>
      </div>
      <p className="resume-contact">
        Software Engineer
        <><span>Dublin, Ireland</span></>
        <><span>praptisetty11@gmail.com</span></>
      </p>

      <section className="resume-section">
        <h2>Profile</h2>
        <p>
          Software Engineer at Microsoft building AI-powered productivity tools using Large Language Models (LLMs)
          for Word Copilot features used by 2M+ users monthly. Experienced in full-stack development, LLM-integrated
          AI systems at scale. Proven track record of designing and delivering features, driving user growth (+150% WAU),
          improving system reliability, and delivering measurable business impact.
        </p>
      </section>

      <section className="resume-section">
        <h2>Github</h2>
        <a href="https://github.com/praptisetty">Check out my Github</a>
      </section>

      <section className="resume-section">
        <h2>Work Experience</h2>

        <article className="resume-role">
          <div className="resume-role-header">
            <h3>Software Engineer II, Word (LLM-based assistants and agents in Copilot)</h3>
            <p>July 2023 - Present</p>
          </div>
          <p className="resume-company">Microsoft, Dublin, Ireland</p>
          <ul>
            <li>Building Grounding and Referencing in Word Copilot for Agentic Experiences </li>
            <li>Built full-stack features for first and follow on iterations of Word Copilot, driving +150% growth in weekly users </li>
            <li>Developed privacy and compliance systems protecting 1M+ AI-generated conversations monthly </li>
            <li>Delivered citation and source attribution for 450k+ conversations weekly improving trust in Gen AI outputs </li>
            <li>Increased system reliability from 75% to 90%+ through service and parsing optimizations </li>
            <li>Led customer feedback loops influencing product roadmap and feature prioritization</li>
          </ul>
        </article>

        <article className="resume-role">
          <div className="resume-role-header">
            <h3>Software Engineer I/II</h3>
            <p>Aug 2020 - Jun 2023</p>
          </div>
          <p className="resume-company">Microsoft, Dublin, Ireland</p>
          <ul>
            <li>Increased user retention by 33%, driving +16K new seats and +14K retained seats monthly</li>
            <li>Built scalable frontend (React, Angular) and backend (.NET, C#) systems</li>
            <li>Worked with Azure, Cosmos DB, and Service Fabric for distributed systems</li>
            <li>Delivered analytics insights using Kusto (KQL), Power BI, and Azure Data Lake</li>
          </ul>
        </article>

        <article className="resume-role">
          <div className="resume-role-header">
            <h3>Software Engineering Intern</h3>
            <p>May 2019 - Aug 2019</p>
          </div>
          <p className="resume-company">Microsoft, Dublin, Ireland</p>
        </article>

        <article className="resume-role">
          <div className="resume-role-header">
            <h3>Software Engineering Intern</h3>
            <p>Jun 2018 - Aug 2018</p>
          </div>
          <p className="resume-company">Microsoft, Dublin, Ireland</p>
        </article>
      </section>

      <section className="resume-section">
        <h2>Education</h2>
        <div className="resume-role-header">
          <h3>Trinity College Dublin</h3>
          <p>2016 - 2020</p>
        </div>
        <p className="resume-company">Bachelor&apos;s Degree in Computer Science</p>
        <p>Second Class Honours (First Division)</p>
      </section>

      <section className="resume-section">
        <h2>Personal Projects</h2>
        <div className="resume-role-header">
          <h3>DemoDock</h3>
          <div className="resume-project-links">
            <a href="https://www.prapti.ie/projects/demodock">View Project</a>
            <a href="https://www.prapti.ie/blog/demodock-architecture">Architecture</a>
          </div>
        </div>
        <p>
          AI-powered visual sandbox for modifying live web applications with natural language, enabling rapid UI
          prototyping, personalized demos, and automated data masking.
        </p>
        <ul>
          <li>Built a full-stack Next.js/Node.js app with a non-destructive browser overlay for editing live pages.</li>
          <li>Engineered a custom reverse-proxy layer to render target sites inside a controlled sandbox.</li>
          <li>Developed a JavaScript bridge for DOM inspection, parent-page commands, and dynamic visual updates.</li>
          <li>Converted plain-English prompts into structured JSON actions using fine-tuned SLM and LLM pipelines.</li>
        </ul>

        <div className="resume-role-header">
          <h3>BrainPocket</h3>
          <div className="resume-project-links">
            <a href="https://www.prapti.ie/projects/brainpocket">View Project</a>
            <a href="https://www.prapti.ie/blog/brainpocket-architecture">Architecture</a>
          </div>
        </div>
        <p>
          Local-first knowledge management app for saving articles, key points, and reading status in a searchable
          domain-based tree.
        </p>
        <ul>
          <li>Built a React app using browser localStorage for offline, backend-free data persistence.</li>
          <li>Designed a tree model that automatically groups saved article URLs by domain.</li>
          <li>Implemented article capture, search, key-point notes, read/unread state, and JSON import/export.</li>
        </ul>

        <div className="resume-role-header">
          <h3>Wheel of Chores</h3>
          <div className="resume-project-links">
            <a href="https://www.prapti.ie/projects/wheel-of-chores">View Project</a>
            <a href="https://www.prapti.ie/blog/wheel-of-chores-architecture">Architecture</a>
          </div>
        </div>
        <p>
          Local-first household chore app that uses randomized, time-budgeted wheels, multi-person assignments,
          and per-person rewards to make shared task planning fair and engaging.
        </p>
        <ul>
          <li>Built a React-based wheel interface for adding people, assigning chores to one or more household members, and filtering each person&apos;s chore list.</li>
          <li>Implemented randomized selection with animated spin feedback, completion attribution, and per-person Chore Coin balances.</li>
          <li>Added localStorage migration plus Capacitor-ready iOS and Android build targets with target-specific app metadata and assets.</li>
        </ul>

        <div className="resume-role-header">
          <h3>Flow Pet RPG</h3>
          <div className="resume-project-links">
            <a href="https://www.prapti.ie/projects/flow-pet-rpg">View Project</a>
            <a href="https://www.prapti.ie/blog/flow-pet-rpg-architecture">Architecture</a>
          </div>
        </div>
        <p>
          Local-first focus timer that turns deep work into an RPG progression loop with pet evolution, loot,
          equipment, and tab-neglect rules.
        </p>
        <ul>
          <li>Built a TypeScript Web Worker timer for reliable focus and break countdowns outside the main UI loop.</li>
          <li>Implemented focus validation using page visibility and window focus before awarding EXP.</li>
          <li>Added persistent RPG state with levels, health, inventory, equipment slots, loot milestones, and pet stages.</li>
        </ul>

        <div className="resume-role-header">
          <h3>Chloromance Plant Tracker</h3>
          <div className="resume-project-links">
            <a href="https://www.prapti.ie/projects/chloromance-tracker">View Project</a>
            <a href="https://www.prapti.ie/blog/chloromance-tracker-architecture">Architecture</a>
          </div>
        </div>
        <p>
          Account-backed plant care dashboard that tracks WiltPower, weather-adjusted hydration, plant locations,
          and watering reminders across web and mobile-style app flows.
        </p>
        <ul>
          <li>Built a React hydration engine that derives live plant status from watering timestamps, weather data, and frequency rules.</li>
          <li>Integrated Supabase authentication and cloud-backed plant sync with user-scoped localStorage fallbacks.</li>
          <li>Implemented Open-Meteo weather modifiers, WiltPower summaries, location filters, batch watering, and notifications.</li>
        </ul>
      </section>

      <section className="resume-section">
        <h2>Technical Skills</h2>
        <ul className="skills-list">
          <li>
            <strong>Languages:</strong> TypeScript, JavaScript, C#, Java, C++, Python
          </li>
          <li>
            <strong>Frameworks:</strong> React, Angular, .NET
          </li>
          <li>
            <strong>Cloud &amp; Data:</strong> Azure, Kusto, Cosmos DB, Azure Data Lake
          </li>
          <li>
            <strong>AI:</strong> Prompting, LLM application design
          </li>
        </ul>
      </section>
    </section>
  );
}

export default Resume;
